import { assert } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key';

/**
 * _Note:_ Although the settings are typed as Map<String, String> in Java, here it's
 * Record<string, unknown>: The setting values are not serialized strings and instead are in their
 * "natural" form (boolean, number, string, etc.). That's because the `WorkbookCommandResultDto` is
 * parsed using the `settingsJsonParser()`, which special-cases "settings" fields.
 *
 * `executeCommand()` on the other hand uses `stringifySettingsValues()` to do the opposite:
 * "settings" are serialized to `Record<string, string>`.
 *
 * @see net/disy/cadenza/workbook/model/IWorkbookItemSettings.java
 * @see {@link https://conf.disy.net/x/ZwsKGQ|Documentation on Workbook Settings in the Wiki}
 */
export type Settings = Record<string, unknown>;
interface SettingsContainer {
  settings?: Settings;
}
export type SettingsOrContainer = Settings | SettingsContainer;

/**
 * Checks whether the given settings define a value for the given key.
 */
export function isSettingSet(settings: SettingsContainer, key: string): boolean;
/**
 * Checks whether the given settings define a value for the given key.
 *
 * @deprecated use isSettingSet(settingsContainer) instead.
 */
export function isSettingSet(settings: Settings, key: string): boolean;
export function isSettingSet(settings: SettingsOrContainer, key: string): boolean {
  return getSetting(settings, key) !== undefined;
}

/**
 * Returns the value of the given settings key or the default value that is defined in the backend. Throws if no default is defined.
 *
 * DISCLAIMER: Default settings are not yet supported for AttributeModifications, yet. Access the
 * corresponding defaultXXXX property of the attribute instead.
 */
export function getSettingOrSettingDefault<T>(settings: SettingsContainer, key: string): T;
/**
 * Returns the value of the given settings key or the default value that is defined in the backend. Throws if no default is defined.
 *
 * DISCLAIMER: Default settings are not yet supported for AttributeModifications, yet. Access the
 * corresponding defaultXXXX property of the attribute instead.
 *
 * @deprecated use getSettingOrSettingDefault(settingsContainer)
 */
export function getSettingOrSettingDefault<T>(settings: Settings, key: string): T;
export function getSettingOrSettingDefault<T>(settings: SettingsOrContainer, key: string) {
  return getSetting(settings, key, getDefaultSetting(settings, key)) as T;
}

/**
 * Returns the value of the given settings key (if any).
 */
export function getSetting<T>(settings: SettingsContainer, key: string): T | undefined;
/**
 * Returns the value of the given settings key (if any).
 *
 * @deprecated use getSetting(settingsContainer, key) instead
 */
export function getSetting<T>(settings: Settings, key: string): T | undefined;
export function getSetting<T>(settings: SettingsOrContainer, key: string): T | undefined;

/**
 * Returns the value of the given settings key or a given default value.
 */
export function getSetting<T>(settings: SettingsContainer, key: string, defaultValue: T): T;
/**
 * Returns the value of the given settings key or a given default value.
 *
 * @deprecated use getSetting(settingsContainer, key, defaultValue)
 */
export function getSetting<T>(settings: Settings, key: string, defaultValue: T): T;
export function getSetting<T>(settings: SettingsOrContainer, key: string, defaultValue?: T) {
  if (key.endsWith('.default')) {
    logger.warn(
      'The convention for the .default keys is encoded in our settings utils. ' +
        'Use getSettingOrSettingDefault() or getDefaultSetting() instead of assembling the key manually.',
    );
  }
  return getSettingInternal(settings, key, defaultValue);
}

/**
 * Returns the default value of the given settings key that is defined in the backend. Throws if no default is defined.
 *
 * DISCLAIMER: Default settings are not yet supported for AttributeModifications, yet. Access the
 * corresponding defaultXXXX property of the attribute instead.
 */
export function getDefaultSetting<T>(settings: SettingsContainer, key: string): T;
/**
 * Returns the default value of the given settings key that is defined in the backend. Throws if no default is defined.
 *
 * DISCLAIMER: Default settings are not yet supported for AttributeModifications, yet. Access the
 * corresponding defaultXXXX property of the attribute instead.
 *
 * @deprecated use getDefaultSetting<T>(settingsContainer, key)
 */
export function getDefaultSetting<T>(settings: Settings, key: string): T;
export function getDefaultSetting<T>(settings: SettingsOrContainer, key: string) {
  const defaultSettingKey = getDefaultSettingKey(key);
  const value = getSettingInternal(settings, defaultSettingKey);
  assert(value !== undefined, `No default value defined for settings key ${key}`);
  return value as T;
}

function getSettingInternal(settings: SettingsOrContainer, key: string, defaultValue?: unknown) {
  return getSettings(settings)[key] ?? defaultValue;
}

export function getDefaultSettingKey(key: string) {
  return `${key}.default`;
}

function getSettings(settings?: SettingsOrContainer) {
  return (settings?.settings ?? settings ?? {}) as Settings;
}

export function getLayerSetting<T>(settings: WorkbookLayerDto, key: LayerSettingsKey): T | undefined;
export function getLayerSetting<T>(settings: WorkbookLayerDto, key: LayerSettingsKey, defaultValue: T): T;
export function getLayerSetting<T>(layerDefinition: WorkbookLayerDto, key: LayerSettingsKey, defaultValue?: T) {
  return getSetting<T>(layerDefinition, LayerSettingsKey.toPersistenceKey(key), defaultValue as T);
}
